import React, { useState, useMemo, useEffect } from 'react';
import { Grid, withStyles } from '@material-ui/core';
import { formatSearchText } from 'utils/search';
import TableContainer from '@material-ui/core/TableContainer';
import PropTypes from 'prop-types';
import Table from 'components/ui/Table';
import tableMetadata from './tableMetaData';
import SsoRoleListFilter from './SsoRoleListFilter';

const styles = (theme) => ({
  mainContainer: {
    padding: '30px',
  },
  tableContainer: {
    maxHeight: 500,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    lineHeight: '16px',
    marginTop: '32px',
  },
});

const SsoRoleList = (props) => {
  const { classes, roles, selectedRoles, setSelectedRoles } = props;
  const [searchFilter, setSearchFilter] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [formState, setFormState] = useState({
    currentPage: 0,
    rowsPerPage: 10,
    filteredRoles: null,
  });

  const filterPermission = useMemo(
    () => (searchFilter, searchKeyword) => {
      let searchResult = roles;

      if (searchFilter !== null && searchFilter !== 1) {
        const isAssignedPermissions = searchFilter === 2 ? true : false;
        searchResult = searchResult.filter((item) => {
          return isAssignedPermissions
            ? selectedRoles.includes(item.roleId)
            : !selectedRoles.includes(item.roleId);
        });
      }

      if (searchKeyword) {
        searchResult = searchResult.filter((item) => {
          const roleName = formatSearchText(item.roleName).toLowerCase();
          const formattedSearch = formatSearchText(
            searchKeyword || ''
          ).toLowerCase();
          return roleName.includes(formattedSearch);
        });
      }

      setFormState((prevState) => ({
        ...prevState,
        currentPage: 0,
        filteredRoles: searchResult,
      }));
    },
    [roles, selectedRoles]
  );

  useEffect(() => {
    filterPermission(searchFilter, searchKeyword);
  }, [selectedRoles, searchFilter, filterPermission]);

  const processRoleData = (roles) => {
    return roles.map((r) => {
      const role = { ...r };
      role.id = role.roleId;
      role.name = role.roleName;
      return role;
    });
  };

  const rolesData =
    formState.filteredRoles === null ? roles : formState.filteredRoles;
  const ssoRoles = useMemo(() => processRoleData(rolesData), [rolesData]);
  const showNoResults =
    formState.filteredRoles !== null && formState.filteredRoles.length === 0;

  const onSearchClick = (e) => {
    e.preventDefault();
    filterPermission(searchFilter, searchKeyword);
  };

  const onSearchFilterChange = (selectedItem) => {
    setSearchFilter(selectedItem.value);
  };

  const onSearchKeywordChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const onSetSelectedItems = (data) => {
    setSelectedRoles(data);
  };

  const handlePageClick = (data) => {
    setFormState((prevState) => ({
      ...prevState,
      currentPage: data.selected,
    }));
  };

  return (
    <Grid className={classes.mainContainer}>
      <SsoRoleListFilter
        onSearchFilterChange={onSearchFilterChange}
        onSearchKeywordChange={onSearchKeywordChange}
        onSearchClick={onSearchClick}
      />
      <Grid container spacing={1} justify="flex-start">
        <Grid item xs={12}>
          <TableContainer className={classes.tableContainer}>
            <Table
              data={ssoRoles}
              metadata={tableMetadata}
              orderBy={'name'}
              classes={{ row: classes.row }}
              showNoResults={showNoResults}
              enableSorting={true}
              showFooter={true}
              noBackground={false}
              showEmptyRows={true}
              currentPage={formState.currentPage}
              rowsPerPage={ssoRoles.length}
              onPageChange={handlePageClick}
              checkboxSelection={true}
              selectedItems={selectedRoles}
              setSelectedItems={onSetSelectedItems}
            />
          </TableContainer>
          <div className={classes.footer}>
            <b>{ssoRoles.length}</b>&nbsp;Results Found
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

SsoRoleList.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.object),
  setSelectedRoles: PropTypes.func.isRequired,
  selectedRoles: PropTypes.arrayOf(PropTypes.object),
};

SsoRoleList.defaultProps = {
  roles: [],
  selectedRoles: [],
};

export default withStyles(styles)(SsoRoleList);
